import { useState } from 'react'
import { PowerButton } from './power-button'
import { useTranslation } from 'react-i18next'
import { ServerCrashIcon, Settings2Icon } from 'lucide-react'
import { Card, CardHeader, CardTitle, CardContent } from '@ui/components/ui/card'
import { Dialog, DialogContent } from '@ui/components/ui/dialog'
import { useDataStore } from '@/lib/machine/use-data-store'
import { FirmwareUpdateTrigger } from '@/components/dashboard/firmware-update/firmware-update-trigger'
import { db } from '@/db/db'
import { useLiveQuery } from 'dexie-react-hooks'
import { MachineCalibrationForm } from './machine-calibration-form'

export const SettingsMachine = () => {
  const { t } = useTranslation()
  const sendMachineSyncFilesAction = useDataStore(s => s.sendMachineSyncFilesAction)
  const machineId = useDataStore(s => s.machineId)
  const refreshData = useDataStore(s => s.refreshData)

  const [calibrationOpen, setCalibrationOpen] = useState(false)

  const installationsInDb = useLiveQuery(async () => {
    return await db.installation.toArray()
  })

  const videosInDb = useLiveQuery(async () => {
    return await db.video.toArray()
  })

  const installationsWithoutDuplicates = installationsInDb
    ? Array.from(
        new Map(
          installationsInDb.map(item => [item.machineId + item.installationTimestamp, item])
        ).values()
      )
    : []

  const videosWithoutDuplicates = videosInDb
    ? Array.from(new Map(videosInDb.map(item => [item.machineId + item.timestamp, item])).values())
    : []

  return (
    <section>
      <Card className="mb-12 p-2 bg-zinc-900/70 rounded-2xl select-none">
        <CardHeader className="pb-0">
          <CardTitle className="flex items-center gap-4">{machineId ?? 'UV XAIR'}</CardTitle>
        </CardHeader>
        <CardContent>
          <span className="text-sm text-muted-foreground">Firmware </span>
          <span className="text-sm font-mono">{refreshData?.SW_VERSION ?? '—'}</span>
        </CardContent>
      </Card>

      <section className="mb-12">
        <div className="grid grid-cols-3 gap-4">
          <PowerButton
            label={t('settings.machine.calibration-button')}
            aria-label={t('settings.machine.calibration-button')}
            onClick={() => setCalibrationOpen(true)}
            className="w-full"
          >
            <Settings2Icon className="w-8" />
          </PowerButton>

          <FirmwareUpdateTrigger />

          <PowerButton
            label={t('settings.machine.delete-machine-data')}
            aria-label={t('settings.machine.delete-machine-data')}
            onClick={() => {
              installationsWithoutDuplicates.forEach(installation => {
                sendMachineSyncFilesAction({
                  in: 'file_remove',
                  file: installation.fileName
                })
              })

              videosWithoutDuplicates.forEach(video => {
                sendMachineSyncFilesAction({
                  in: 'file_remove',
                  file: video.fileName
                })
              })
            }}
            className="w-full"
          >
            <ServerCrashIcon className="w-8" />
          </PowerButton>
        </div>
      </section>

      <Dialog open={calibrationOpen} onOpenChange={setCalibrationOpen}>
        <DialogContent
          className="min-w-[50vw] max-h-1/2 p-16 rounded-2xl [&>button:last-child]:hidden"
          onInteractOutside={e => {
            e.preventDefault()
          }}
        >
          <MachineCalibrationForm onClose={() => setCalibrationOpen(false)} />
        </DialogContent>
      </Dialog>
    </section>
  )
}
